import React, { useEffect, useState } from 'react'

interface HolidayInfo {
  date: string // YYYY-MM-DD format
  name: string
  type: 'holiday' | 'workday' // holiday: 公共假期, workday: 调休工作日
}

interface CalendarProps {
  selectedDate: Date | null
  onDateSelect: (date: Date | null) => void
  label?: string
  helpText?: string
  holidays?: HolidayInfo[]
  defaultCollapsed?: boolean
  // 可选：高亮多个日期区间
  ranges?: Array<{ start: string; end: string; name: string; color?: string }>
  // 可选：当没有 selectedDate 时，用于设定初始显示的月份
  initialMonthDate?: string
  // 可选：是否显示内置图例（周末/法定节假日/调休/选中日期），默认显示
  showDefaultLegend?: boolean
  // 可选：是否允许折叠，默认允许；为 false 时始终展开
  collapsible?: boolean
  // 可选：是否显示顶部标题/可折叠头部，默认显示；为 false 时隐藏头部
  showHeader?: boolean
  // 可选：高亮特定日期数组，使用自定义颜色
  highlightDates?: Array<{ date: string; color: string; name?: string }>
}

const Calendar: React.FC<CalendarProps> = ({ 
  selectedDate, 
  onDateSelect, 
  label = "选择日期",
  helpText = "请选择日期",
  holidays = [],
  defaultCollapsed = true,
  ranges = [],
  initialMonthDate,
  showDefaultLegend = true,
  collapsible = true,
  showHeader = true,
  highlightDates = [],
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [isCollapsed, setIsCollapsed] = useState(collapsible ? defaultCollapsed : false)
  const [showYearMonthSelector, setShowYearMonthSelector] = useState(false)

  // 当defaultCollapsed变化时，更新isCollapsed状态
  useEffect(() => {
    setIsCollapsed(defaultCollapsed)
  }, [defaultCollapsed])

  // 当selectedDate变化时，更新currentMonth到对应月份，并折叠日历
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))
      // 如果有选中的日期，自动折叠日历
      setIsCollapsed(true)
    }
  }, [selectedDate])

  // 当提供 initialMonthDate 且没有 selectedDate 时，设置初始显示月份
  useEffect(() => {
    if (!selectedDate && initialMonthDate) {
      const [y, m] = initialMonthDate.split('-').map((v) => parseInt(v, 10))
      if (Number.isFinite(y) && Number.isFinite(m)) {
        setCurrentMonth(new Date(y, Math.max(0, m - 1), 1))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMonthDate])

  const handleDateSelect = (date: Date) => {
    console.log('🗓️ Calendar handleDateSelect called with:', date.toDateString(), date.toISOString().split('T')[0])
    setShowYearMonthSelector(false)
    // 选择日期后自动收缩日历
    if (collapsible) {
      setIsCollapsed(true)
    }
    onDateSelect(date)
  }

  const getHolidayInfo = (date: Date): HolidayInfo | null => {
    // 使用本地日期格式化，避免时区转换问题
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}` // YYYY-MM-DD format
    return holidays.find(holiday => holiday.date === dateString) || null
  }

  // 检查某个日期是否在 highlightDates 中
  const getHighlightInfo = (date: Date): { color: string; name?: string } | null => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}` // YYYY-MM-DD format
    return highlightDates.find(item => item.date === dateString) || null
  }

  // 判断某天属于哪个区间，返回区间索引
  const getRangeIndexForDate = (date: Date): number => {
    if (!ranges || ranges.length === 0) return -1
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    const ds = `${y}-${m}-${d}`
    for (let i = 0; i < ranges.length; i++) {
      const r = ranges[i]
      if (!r?.start || !r?.end) continue
      if (ds >= r.start && ds <= r.end) return i
    }
    return -1
  }

  // 从 CSS 变量解析调色板，避免硬编码
  const CSS_RANGE_COLOR_VARS = [
    '--calendar-color-blue',
    '--calendar-color-green',
    '--calendar-color-purple',
    '--calendar-color-cyan-blue',
    '--calendar-color-deep-purple',
    '--calendar-color-teal-green',
    '--calendar-color-gold-brown',
    '--calendar-color-fuchsia',
  ] as const

  const getCssVar = (name: string): string => {
    if (typeof window === 'undefined') return ''
    const styles = getComputedStyle(document.documentElement)
    const v = styles.getPropertyValue(name).trim()
    if (v) return v
    // 兼容可能的单短横线历史写法
    if (name.startsWith('--')) {
      const legacy = styles.getPropertyValue(name.replace(/^--/, '-')).trim()
      if (legacy) return legacy
    }
    return ''
  }

  const [paletteColors, setPaletteColors] = useState<string[]>([])

  useEffect(() => {
    const resolved = CSS_RANGE_COLOR_VARS.map((n) => getCssVar(n))
    // 若部分变量缺失，使用设计系统现有变量做兜底，避免出现空色
    const fallbacks = [
      'var(--color-blue)',
      'var(--color-success)',
      'var(--color-secondary)',
      'var(--color-primary)',
      'var(--success-color-light)',
      'var(--warning-color)',
      'var(--accent-gold)',
      'var(--color-blue-dark)',
    ]
    const final = resolved.map((c, i) => (c && c.length > 0 ? c : fallbacks[i % fallbacks.length]))
    setPaletteColors(final)
  }, [])

  const getRangeColor = (idx: number): string => {
    if (idx < 0) return ''
    return ranges[idx]?.color || paletteColors[idx % (paletteColors.length || 1)] || ''
  }

  const renderCalendar = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())

    const days = []
    for (let i = 0; i < 42; i++) {
      // Use a more reliable date calculation that avoids timezone issues
      const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i)
      
      // Debug: Log the first few dates to check calculation
      if (i < 7) {
        console.log(`🗓️ Day ${i}: ${date.toDateString()} (${date.toISOString().split('T')[0]})`)
      }
      
      const isCurrentMonth = date.getMonth() === month
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
      const isWeekend = date.getDay() === 0 || date.getDay() === 6
      const isToday = date.toDateString() === new Date().toDateString()
      const holidayInfo = getHolidayInfo(date)
      const rangeIndex = getRangeIndexForDate(date)
      const highlightInfo = getHighlightInfo(date)
      
      let dayClasses = 'calendar-day'
      if (!isCurrentMonth) dayClasses += ' other-month'
      if (isSelected) dayClasses += ' selected'
      if (isToday) dayClasses += ' today'
      
      // Holiday and workday styling takes precedence over weekend
      if (holidayInfo) {
        if (holidayInfo.type === 'holiday') {
          dayClasses += ' public-holiday'
        } else if (holidayInfo.type === 'workday') {
          dayClasses += ' work-adjustment'
        }
      } else if (isWeekend) {
        dayClasses += ' weekend'
      }

      const dayStyle: React.CSSProperties = {}
      
      // 样式优先级：
      // 1. highlightDates（假期顺延）优先级最高
      // 2. rangeIndex（产假/奖励假范围）优先级次之
      // 3. 如果调休日期在产假范围内，显示产假背景色（覆盖调休紫色）
      if (highlightInfo) {
        dayStyle.background = `${highlightInfo.color}` // 使用自定义颜色
      } else if (rangeIndex >= 0) {
        // 产假/奖励假范围内的日期，包括调休日期，都显示假期背景色
        const color = getRangeColor(rangeIndex)
        dayStyle.background = `${color}44` // 稍强的透明度
        // dayStyle.boxShadow = `inset 0 0 0 2px ${color}99` // 移除内边框以避免每一天有边线
      }

      days.push(
        <div
          key={i}
          className={dayClasses}
          onClick={() => isCurrentMonth && handleDateSelect(date)}
          title={highlightInfo?.name || holidayInfo?.name || ''}
          style={dayStyle}
        >
          {date.getDate()}
        </div>
      )
    }

    return days
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(currentMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(currentMonth.getMonth() + 1)
    }
    setCurrentMonth(newMonth)
  }

  const handleYearChange = (year: number) => {
    const newMonth = new Date(currentMonth)
    newMonth.setFullYear(year)
    setCurrentMonth(newMonth)
    onDateSelect(null)
  }

  const handleMonthChange = (month: number) => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(month)
    setCurrentMonth(newMonth)
    onDateSelect(null)
  }

  const toggleYearMonthSelector = () => {
    setShowYearMonthSelector(!showYearMonthSelector)
  }

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear - 10; i <= currentYear + 10; i++) {
      years.push(i)
    }
    return years
  }

  const monthNames = [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ]

  const toggleCalendar = () => {
    if (!collapsible) return
    setIsCollapsed(!isCollapsed)
  }

  const getDisplayText = () => {
    if (selectedDate) {
      return selectedDate.toLocaleDateString('zh-CN')
    }
    return helpText
  }

  return (
    <div className="form-group">
      {showHeader && (
        <label className="form-label calendar-toggle" onClick={toggleCalendar}>
          <i className="fas fa-calendar-day" />
          {label}
          <span className="selected-date-text">{getDisplayText()}</span>
          {collapsible && (
            <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'} toggle-icon`} />
          )}
        </label>
      )}
      {((!collapsible) || (!showHeader) || (!isCollapsed)) && (
        <div className="calendar-container">
        <div className="calendar-header">
          <button 
            type="button" 
            className="calendar-nav"
            onClick={() => navigateMonth('prev')}
          >
            <i className="fas fa-chevron-left" />
          </button>
          <div className="calendar-title" onClick={toggleYearMonthSelector} style={{ cursor: 'pointer' }}>
            {showYearMonthSelector ? (
              <div className="year-month-selector" onClick={(e) => e.stopPropagation()}>
                <select 
                  value={currentMonth.getFullYear()} 
                  onChange={(e) => handleYearChange(parseInt(e.target.value))}
                  className="year-select"
                >
                  {generateYearOptions().map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
                <select 
                  value={currentMonth.getMonth()} 
                  onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                  className="month-select"
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>{month}</option>
                  ))}
                </select>
              </div>
            ) : (
              <span>
                {currentMonth.getFullYear()}年{currentMonth.getMonth() + 1}月
                <i className="fas fa-caret-down" style={{ marginLeft: '8px', fontSize: '12px' }} />
              </span>
            )}
          </div>
          <button 
            type="button" 
            className="calendar-nav"
            onClick={() => navigateMonth('next')}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
        <div className="calendar-weekdays">
          {['日', '一', '二', '三', '四', '五', '六'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {renderCalendar()}
        </div>
        {showDefaultLegend && (
          <div className="calendar-legend">
            <div className="legend-item">
              <div className="legend-color weekend"></div>
              <span>周末</span>
            </div>
            <div className="legend-item">
              <div className="legend-color holiday"></div>
              <span>法定节假日</span>
            </div>
            <div className="legend-item">
              <div className="legend-color work-adjustment"></div>
              <span>调休</span>
            </div>
            <div className="legend-item">
              <div className="legend-color selected"></div>
              <span>选中日期</span>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  )
}

export default Calendar
